import { computed } from 'vue'

import useRef from '@nicolaudie-group/ng-ui-kit/composables/useRef'
import useTranslate from '@/composables/useTranslate'

export const LEVEL_ERROR = 'error'
export const LEVEL_WARNING = 'warning'
export const LEVEL_INFO = 'info'
export const LEVEL_SUCCESS = 'success'

function useReportBadge(report) {
  const reportRef = useRef(report)

  const { translate, translationsLoaded } = useTranslate()

  const errorsCount = computed(() => (
    reportRef.value?.errors?.length || 0
  ))

  const warningsCount = computed(() => (
    reportRef.value?.warnings?.length || 0
  ))

  const infosCount = computed(() => (
    reportRef.value?.infos?.length || 0
  ))

  const level = computed(() => {
    const errors = errorsCount.value
    const warnings = warningsCount.value
    const infos = infosCount.value
    if (errors) {
      return LEVEL_ERROR
    }
    if (warnings) {
      return LEVEL_WARNING
    }
    if (infos) {
      return LEVEL_INFO
    }
    return LEVEL_SUCCESS
  })

  const icon = computed(() => {
    switch (level.value) {
      case LEVEL_ERROR:
        return 'warning'
      case LEVEL_WARNING:
        return 'warning'
      case LEVEL_INFO:
        return 'info'
      case LEVEL_SUCCESS:
      default:
        return 'checked'
    }
  })

  const errorText = computed(() => `${errorsCount.value} ${translate('pb2.reportBadge.errors')}`)
  const warningText = computed(() => `${warningsCount.value} ${translate('pb2.reportBadge.warnings')}`)
  const infoText = computed(() => `${infosCount.value} ${translate('pb2.reportBadge.infos')}`)

  const shortText = computed(() => {
    const error = errorText.value
    const warning = warningText.value
    const info = infoText.value
    const translationsLoadedValue = translationsLoaded.value

    switch (level.value) {
      case LEVEL_ERROR:
        return error
      case LEVEL_WARNING:
        return warning
      case LEVEL_INFO:
        return info
      case LEVEL_SUCCESS:
      default:
        return translationsLoadedValue ? translate('pb2.reportBadge.successShort') : 'All good !'
    }
  })

  const text = computed(() => {
    const errors = errorsCount.value
    const warnings = warningsCount.value
    const infos = infosCount.value

    const errorTxt = errorText.value
    const warningTxt = warningText.value
    const infoTxt = infoText.value

    const translationsLoadedValue = translationsLoaded.value

    const result = []

    if (errors) {
      result.push(errorTxt)
    }
    if (warnings) {
      result.push(warningTxt)
    }
    if (infos) {
      result.push(infoTxt)
    }

    if (result.length) {
      const contains = translationsLoadedValue ? translate('pb2.reportBadge.contains') : 'Contains'
      const and = translationsLoadedValue ? translate('pb2.reportBadge.and') : 'and'
      const last = result.splice(result.length - 1, 1)
      return `${contains} ${result.join(', ')}${result.length ? ` ${and} ` : ''}${last}`
    }

    return translationsLoadedValue ? translate('pb2.reportBadge.success') : 'No issues found'
  })

  const success = computed(() => level.value === LEVEL_SUCCESS)

  return {
    errorsCount,
    warningsCount,
    infosCount,
    level,
    icon,
    errorText,
    warningText,
    infoText,
    shortText,
    text,
    success,
  }
}

export default useReportBadge
